import {
  listFacilities,
  listPharmacies,
} from "../../services/librariesCatalogService";

/** Field mapping for pharmacy / hospital patient demographics. */
export const PRIMARY_FACILITY_FIELD_CONFIG = {
  pharmacy: {
    nameField: "pharmacyName",
    typeField: "pharmacyType",
    addressField: "pharmacyAddress",
    cityField: "pharmacyCity",
    stateField: "pharmacyState",
    zipField: "pharmacyZip",
    phoneField: "pharmacyPhone",
    faxField: "pharmacyFax",
    nameLabel: "Pharmacy Name",
    typeLabel: "Pharmacy Type",
    placeholder: "Search or type pharmacy name",
    hint: "Pick from library to auto-fill, or type a custom name",
  },
  hospital: {
    nameField: "hospitalName",
    typeField: "hospitalType",
    addressField: "hospitalAddress",
    cityField: "hospitalCity",
    stateField: "hospitalState",
    zipField: "hospitalZip",
    phoneField: "hospitalPhone",
    faxField: "hospitalFax",
    nameLabel: "Hospital Name",
    typeLabel: "Hospital Type",
    placeholder: "Search or type hospital name",
    hint: "Pick from library to auto-fill, or type a custom name",
  },
};

const SUGGEST_LIMIT = 12;

/**
 * Apply a Libraries catalog row onto react-hook-form patient fields.
 * Free-typed names (no catalog match) are saved as-is on submit.
 */
export function applyPrimaryFacilityCatalogRow(variant, row, setValue) {
  if (!row || !setValue) return;
  const c = PRIMARY_FACILITY_FIELD_CONFIG[variant];
  if (!c) return;

  if (variant === "pharmacy") {
    setValue(c.nameField, row.pharmacyName || "");
    setValue(c.typeField, row.pharmacyType || "");
    setValue(c.addressField, row.address || "");
    setValue(c.cityField, row.city || "");
    setValue(c.stateField, row.state || "");
    setValue(c.zipField, row.zip || "");
    setValue(c.phoneField, row.phone || "");
    setValue(c.faxField, row.fax || "");
    return;
  }

  setValue(c.nameField, row.facilityName || "");
  setValue(c.typeField, row.facilityType || "");
}

export async function searchPrimaryFacilityCatalog(variant, query = "", { signal } = {}) {
  const trimmed = String(query || "").trim();
  const params = {
    status: "Active",
    limit: SUGGEST_LIMIT,
    nameOnly: true,
    ...(trimmed ? { search: trimmed } : {}),
  };
  const fetchOptions = { signal };

  if (variant === "pharmacy") {
    const res = await listPharmacies(
      {
        ...params,
        sortBy: "pharmacyName",
        sortOrder: "asc",
      },
      fetchOptions,
    );
    return res?.items || [];
  }

  const res = await listFacilities(
    {
      ...params,
      sortBy: "facilityName",
      sortOrder: "asc",
    },
    fetchOptions,
  );
  return res?.items || [];
}

export function getPrimaryFacilityOptionLabel(variant, row) {
  if (!row) return "";
  return variant === "pharmacy" ? row.pharmacyName : row.facilityName;
}

export function getPrimaryFacilityOptionKey(variant, row) {
  if (!row?.id) return getPrimaryFacilityOptionLabel(variant, row);
  return `${variant}-${row.id}`;
}

export function getPrimaryFacilityOptionSubtitle(variant, row) {
  if (!row) return "";
  if (variant === "pharmacy") {
    const parts = [
      row.pharmacyType,
      [row.city, row.state].filter(Boolean).join(", "),
    ].filter(Boolean);
    return parts.join(" · ");
  }
  const parts = [row.facilityType, row.facilityCode, row.npiNumber].filter(
    Boolean,
  );
  return parts.join(" · ");
}

export const PRIMARY_FACILITY_FIELD_KEYS = {
  pharmacy: [
    "pharmacyName",
    "pharmacyAddress",
    "pharmacyCity",
    "pharmacyState",
    "pharmacyZip",
    "pharmacyPhone",
    "pharmacyFax",
    "pharmacyType",
  ],
  hospital: [
    "hospitalName",
    "hospitalAddress",
    "hospitalCity",
    "hospitalState",
    "hospitalZip",
    "hospitalPhone",
    "hospitalFax",
    "hospitalType",
  ],
};
